import React, { useState, useEffect } from 'react';
import { AuthProvider } from 'contexts/AuthContext';
import { User } from 'lib/User';
import { Routes, Navigate, Route, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import weekday from 'dayjs/plugin/weekday';
import 'dayjs/locale/fr';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useIntercom } from 'react-use-intercom';
import { Toaster } from 'react-hot-toast';

import { UserContext } from 'constants/user-context';
import { SPYEE_TOKEN, SPYEE_REFRESH_TOKEN } from '../src/constants/index';
import { hasAccessToken } from 'data/connection/storage';
import { isAuthenticationError } from 'data/connection/auth-errors';

import { Navbar, CenterPanel } from 'wrappers/index';

import { Login } from 'pages/Login';
import { DownloadMobileApp } from 'pages/DownloadMobileApp';
import { SetPassword } from 'pages/SetPassword/SetPassword';
import { RequestResetPassword } from 'pages/ResetPassword/RequestResetPassword';
import { ResetPassword } from 'pages/ResetPassword/ResetPassword';
import { Sven } from 'pages/Sven';
import { SplitScreen } from 'pages/SplitScreen';
import { Loading } from 'components/Loading';

import { Spybox } from 'ui/Spybox';
import { TotemLoadingStyle } from 'styles/pages/totem.styles';
import { Signup } from 'pages/Signup/Signup';
import { EmployeeEmailValidation } from 'pages/Settings/SettingsPage/SettingsTeam/EmployeeEmailValidation';
import { OrderDetails } from 'pages/Order/OrderDetails';
import { DeliveryTracking } from 'pages/Delivery/DeliveryTracking';

import { TotemContainer, Wrapper } from 'styles';

import { ENV_PREPROD, ENV_PROD } from 'constants/environment-constants';

import { identify } from './tracking/segment';
import { ModalContext } from 'contexts/ModalContext';
import { TotemClosedDaysContext } from 'contexts/TotemClosedDaysContext';
import type { TotemEnv } from './types/totem-env';
import { ErrorHandler } from 'utils/error-handler';
import { useGetOrganizationQuery, useGetTotemClosedDaysQuery, useGetUserQuery } from 'data/__generated__';
dayjs.locale('fr');

dayjs.extend(relativeTime);
dayjs.extend(weekday);
dayjs.extend(customParseFormat);

export const App: React.FC = () => {
    const getSkipValue = (): boolean => !hasAccessToken();
    const env = window.__TOTENV__ as TotemEnv;
    // eslint-disable-next-line
    const { boot: bootIntercom } = env.E2E ? { boot: () => {} } : useIntercom();
    const location = useLocation();
    const pathname: string = location.pathname;
    const state: any = location.state || {};
    const from: string | undefined = state?.from;
    const [skip, setSkip] = useState<boolean>(getSkipValue());
    const [initialized, setInitialized] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modal, setModal] = useState<any>(null);

    const showNavbar = !/(login|signup|set-password|recover-password|verify-email|sven)/.test(pathname) && !skip;

    const {
        loading: userLoading,
        error: userError,
        data: userData,
    } = useGetUserQuery({
        skip,
    });
    const {
        loading: orgLoading,
        error: orgError,
        data: orgData,
    } = useGetOrganizationQuery({
        skip,
    });
    const {
        loading: totemDaysLoading,
        error: totemDaysError,
        data: totemDaysData,
    } = useGetTotemClosedDaysQuery({ skip });

    const loading: boolean = userLoading || orgLoading || totemDaysLoading;

    useEffect(() => {
        if (!initialized && !userLoading && userData?.user && userData?.user?.profile) {
            if (!env.E2E) {
                const {
                    _id,
                    createdAt,
                    username,
                    profile: { firstname, lastname, organizationName, organizationId },
                    roles,
                    visitedSites,
                } = userData?.user || {};
                identify({
                    createdAt: createdAt / 1000,
                    email: username,
                    id: _id,
                    name: `${firstname} ${lastname}`,
                });
                bootIntercom({
                    company: {
                        companyId: organizationId ?? '',
                        name: organizationName ?? '',
                    },
                    email: username,
                    name: `${firstname} ${lastname}`,
                    userId: _id,
                    customAttributes: {
                        isUserC: roles.global.includes('Employee') || !!visitedSites?.length,
                        isUserB: !roles.global.includes('Employee'),
                        totemUserLink: `https://admin.totem.co/user/${_id}`,
                    },
                });
            }
            setInitialized(true);
        }

        // We want a different icon in PROD than in other environments
        if (window.__TOTENV__.ENV !== ENV_PROD) {
            // 1. Update icon
            const largeFavicon = document.getElementById('large-favicon') as HTMLLinkElement | null;
            const smallFavicon = document.getElementById('small-favicon') as HTMLLinkElement | null;

            if (largeFavicon) largeFavicon.href = '/favicon-preprod-32x32.png';
            if (smallFavicon) smallFavicon.href = '/favicon-preprod-16x16.png';

            // 2. Update page title
            const isSignupPage = window.location.pathname.includes('signup');
            const title = isSignupPage ? 'TOTEM - Création de votre compte' : 'TOTEM | Application Web';
            const pageTitle = document.getElementById('page-title');
            if (pageTitle) {
                pageTitle.innerHTML = (window.__TOTENV__.ENV === ENV_PREPROD ? '[preprod] ' : '[local] ') + title;
            }

            // 3. Update meta description
            const regularDescription =
                "Un frigo connecté, la livraison de produits au bureau, tout ça grâce à l'application mobile et la marketplace TOTEM. Ne restez pas sur votre faim, découvrez nos produits !";
            const signupDescription =
                'Créez votre espace client pour suivre votre TOTEM, commander des produits et bénéficier des meilleurs produits aux meilleurs prix !';
            const desc = isSignupPage ? signupDescription : regularDescription;
            const metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc) metaDesc.setAttribute('content', desc);
        }
    }, [userLoading, initialized, userData, loading, bootIntercom, env.E2E]);

    if (loading) {
        return (
            <TotemLoadingStyle>
                <Loading />
            </TotemLoadingStyle>
        );
    }

    // Auth errors are already handled by the Apollo chain (token refresh, or logout + redirect to
    // /login) — reaching the ErrorHandler boundary would show the "Oups" screen instead of login.
    const error =
        [userError, orgError, totemDaysError].find((queryError) => queryError && !isAuthenticationError(queryError)) ??
        null;

    const { user = null } = userData || {};
    const { organizationOfUser: organization = null } = orgData || {};
    const { getTotemClosedDays: totemClosedDays = [] } = totemDaysData || {};

    if (user) {
        // @ts-ignore
        User?.setUser(user);
    }

    const handleAuthCallback = (): void => {
        const skipValue = getSkipValue();
        setSkip(skipValue);
    };

    // This needs to be a function as a const depending on window is not automatically refreshed when window changes
    function getShouldDisplayExitSpy(): boolean {
        return !!window.TOTEM?.storage?.get?.(SPYEE_TOKEN);
    }

    const handleExitSpy = async (): Promise<void> => {
        await window.TOTEM?.storage?.remove?.(SPYEE_TOKEN);
        await window.TOTEM?.storage?.remove?.(SPYEE_REFRESH_TOKEN);
        !getShouldDisplayExitSpy() && window.location.replace('/spying');
    };

    return (
        <AuthProvider onAuthChange={handleAuthCallback}>
            <UserContext.Provider
                value={{
                    organization: !skip ? organization : null,
                    user: !skip ? user : null,
                }}
            >
                <ModalContext.Provider
                    value={{
                        isModalOpen,
                        setIsModalOpen,
                        modal,
                        setModal,
                    }}
                >
                    {User.isGlobalEmployee() ? (
                        // need to put this page inside the UserContext.Provider to let the employee logout
                        <DownloadMobileApp />
                    ) : (
                        <>
                            <Toaster />
                            <ErrorHandler>
                                {error ? (
                                    <div>
                                        {(function () {
                                            throw error;
                                        })()}
                                    </div>
                                ) : (
                                    <TotemContainer>
                                        {showNavbar && <Navbar />}
                                        <Wrapper>
                                            {user ? (
                                                <TotemClosedDaysContext.Provider value={!skip ? totemClosedDays : []}>
                                                    <Routes>
                                                        <Route path="/sven" element={<Sven />} />
                                                        <Route path="/login" element={<Navigate to={from || '/'} />} />
                                                        <Route
                                                            path="/set-password/:stepId"
                                                            element={<SetPassword setSkip={setSkip} />}
                                                        />
                                                        <Route
                                                            path="/recover-password"
                                                            element={<RequestResetPassword />}
                                                        />
                                                        <Route
                                                            path="/recover-password/:token"
                                                            element={<ResetPassword setSkip={setSkip} />}
                                                        />
                                                        <Route
                                                            path="/validate-employee/:token"
                                                            element={<EmployeeEmailValidation setSkip={setSkip} />}
                                                        />
                                                        <Route path="/order/:orderId?" element={<OrderDetails />} />
                                                        <Route
                                                            path="/suivi/:deliveryId"
                                                            element={<DeliveryTracking />}
                                                        />
                                                        <Route path="*" element={<CenterPanel />} />
                                                    </Routes>
                                                </TotemClosedDaysContext.Provider>
                                            ) : (
                                                <Routes>
                                                    <Route path="/" element={<SplitScreen />} />
                                                    <Route path="/login" element={<Login />} />
                                                    <Route path="/sven" element={<Sven />} />
                                                    <Route path="/signup" element={<Signup />} />
                                                    <Route
                                                        path="/set-password/:stepId"
                                                        element={<SetPassword setSkip={setSkip} />}
                                                    />
                                                    <Route
                                                        path="/recover-password"
                                                        element={<RequestResetPassword />}
                                                    />
                                                    <Route
                                                        path="/recover-password/:token"
                                                        element={<ResetPassword setSkip={setSkip} />}
                                                    />
                                                    {/* Seul un admin connecté valide un rattachement : connexion, puis
                                                    retour au lien (cf. la route /login de la partie connectée). */}
                                                    <Route
                                                        path="/validate-employee/:token"
                                                        element={<Navigate to="/login" state={{ from: pathname }} />}
                                                    />
                                                    <Route path="*" element={<Navigate to="/login" />} />
                                                </Routes>
                                            )}
                                        </Wrapper>
                                        {user && getShouldDisplayExitSpy() && (
                                            <Spybox onClick={handleExitSpy} organizationName={organization?.name} />
                                        )}
                                    </TotemContainer>
                                )}
                            </ErrorHandler>
                        </>
                    )}
                </ModalContext.Provider>
            </UserContext.Provider>
        </AuthProvider>
    );
};
